/**
 * Dashboard Service
 * Handles dashboard statistics and summary data
 */

import type { ApiResponse, DashboardStats } from '../types/api';
import { BaseService } from './BaseService';

export interface DashboardCardData {
  finance: {
    lastInsight: string | null;
    insightType?: 'insight' | 'alert' | 'success';
  };
  extraIncome: {
    activeSuggestions: number;
    hasGeneratedSuggestions: boolean;
  };
  businessPlan: {
    lastPlan: string | null;
    completionPercentage: number;
    planId?: string;
  };
}

export class DashboardService extends BaseService {
  /**
   * Get dashboard statistics (legacy method)
   */
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    try {
      return await this.fetchApi<DashboardStats>('/api/dashboard/stats', {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      return this.createErrorResponse(
        'Erro ao carregar estatísticas do dashboard.',
        error instanceof Error ? error.message : 'Dashboard stats fetch failed'
      );
    }
  }

  /**
   * Get specific data for dashboard cards
   */
  async getDashboardCardData(): Promise<ApiResponse<DashboardCardData>> {
    try {
      const [financeInsights, extraIncomeData, businessPlansData] = await Promise.all([
        this.getFinanceInsights(),
        this.getExtraIncomeData(), 
        this.getBusinessPlanData()
      ]);

      const cardData: DashboardCardData = {
        finance: financeInsights,
        extraIncome: extraIncomeData,
        businessPlan: businessPlansData
      };

      return {
        success: true,
        data: cardData,
        message: 'Dashboard card data retrieved successfully'
      };
    } catch (error) {
      return this.createErrorResponse(
        'Erro ao carregar dados do dashboard.',
        error instanceof Error ? error.message : 'Dashboard card data fetch failed'
      );
    }
  }

  /**
   * Get finance insights for the finance card
   */
  private async getFinanceInsights() {
    try {
      const response = await this.fetchApi<any[]>('/api/finance/insights', {
        headers: this.getAuthHeaders(),
      });

      if (response.success && response.data && response.data.length > 0) {
        // Get the most recent insight/alert
        const latestInsight = response.data[0];
        return {
          lastInsight: latestInsight.title || latestInsight.description,
          insightType: latestInsight.type as 'insight' | 'alert' | 'success'
        };
      }

      return {
        lastInsight: 'Registre suas transações para obter insights',
        insightType: 'insight' as const
      };
    } catch (error) {
      return {
        lastInsight: 'Erro ao carregar insights financeiros',
        insightType: 'alert' as const
      };
    }
  }

  /**
   * Get extra income data for the extra income card
   */
  private async getExtraIncomeData() {
    try {
      const response = await this.fetchApi<any[]>('/api/extra-income/ai-suggestions', {
        headers: this.getAuthHeaders(),
      });

      if (response.success && response.data) {
        return {
          activeSuggestions: response.data.length,
          hasGeneratedSuggestions: response.data.length > 0
        };
      }

      return {
        activeSuggestions: 0,
        hasGeneratedSuggestions: false
      };
    } catch (error) {
      return {
        activeSuggestions: 0,
        hasGeneratedSuggestions: false
      };
    }
  }

  /**
   * Get business plan data for the business plan card
   */
  private async getBusinessPlanData() {
    try {
      const response = await this.fetchApi<any[]>('/api/business-plans', {
        headers: this.getAuthHeaders(),
      });

      if (response.success && response.data && response.data.length > 0) {
        // Get the most recent business plan
        const latestPlan = response.data[0]; // API already orders by updated_at DESC
        return {
          lastPlan: latestPlan.title,
          completionPercentage: latestPlan.completion_percentage || 0,
          planId: latestPlan.id
        };
      }

      return {
        lastPlan: null,
        completionPercentage: 0
      };
    } catch (error) {
      return {
        lastPlan: 'Erro ao carregar planos',
        completionPercentage: 0
      };
    }
  }
}

// Export singleton instance
export const dashboardService = new DashboardService();
